import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertTriangle,
  Activity,
  Clock,
  Brain,
  Gauge,
  Loader2,
  ShieldAlert,
  Sparkles,
  ListChecks,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns';
import { RiskGauge } from './RiskGauge';
import { ProjectRiskTrend } from './ProjectRiskTrend';
import { RiskFactorsPanel, WorkloadFactor, BudgetFactor } from './RiskFactorsPanel';
import { RiskMitigationPanel } from './RiskMitigationPanel';
import { RiskWhyPanel } from './RiskWhyPanel';
import {
  RiskSignalRow,
  SEVERITY_SCORE,
  dedupLatestPerKey,
  getRiskColor,
  getRiskBgColor,
  getRiskLabel,
  getSeverityBadgeColor,
  getSeverityDotColor,
  humanizeRule,
} from './riskUtils';

const LEADERSHIP_ROLES = ['pmo', 'executive', 'tenant_admin', 'admin'];

interface TaskRow {
  external_id: string;
  title: string;
  status: string | null;
  due_date: string | null;
  project_external_id: string | null;
  release_id: string | null;
}

interface ReleaseRow {
  id: string;
  name: string;
}

interface ProjectRow {
  id: string;
  external_id: string;
  name: string;
  budget: number | null;
  spent: number | null;
  team_data: unknown;
}

interface OverrideRow {
  project_id: string;
  technical_complexity: number | null;
  scope_creep: number | null;
}

interface MonthlyTrendPoint {
  month: string;
  avgRisk: number;
  highRiskProjects: number;
  mitigated: number;
}

const monthlyChartConfig = {
  avgRisk: { label: 'Avg Risk', color: 'hsl(var(--primary))' },
  highRiskProjects: { label: 'High Risk Projects', color: 'hsl(var(--destructive))' },
  mitigated: { label: 'Risks Mitigated', color: 'hsl(var(--emerald-500, 152 76% 40%))' },
};

const RiskPredictionView = () => {
  const { tenantId } = useAuth();
  const { role, loading: roleLoading } = useUserRole();
  const canManage = !roleLoading && LEADERSHIP_ROLES.includes(role ?? '');

  const [loading, setLoading] = useState(true);
  const [rawSignals, setRawSignals] = useState<RiskSignalRow[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [releases, setReleases] = useState<ReleaseRow[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [overrides, setOverrides] = useState<Record<string, OverrideRow>>({});
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);

    // PostgREST caps a single response at (by default) 1000 rows. Every
    // project-attribution and monthly-reconstruction computation below
    // depends on having the COMPLETE tasks_sync/risk_signals sets — a
    // silent truncation would misattribute signals to "no project" and
    // undercount overdue tasks, not just paginate a list — so page
    // through both explicitly rather than risk it.
    const PAGE_SIZE = 1000;
    async function fetchAllRows<T>(
      queryPage: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
    ): Promise<T[]> {
      const all: T[] = [];
      let from = 0;
      let hasMore = true;
      while (hasMore) {
        const { data, error } = await queryPage(from, from + PAGE_SIZE - 1);
        if (error || !data) break;
        all.push(...data);
        hasMore = data.length === PAGE_SIZE;
        from += PAGE_SIZE;
      }
      return all;
    }

    const [signalsData, tasksData, releasesRes, projectsRes, overridesRes] = await Promise.all([
      fetchAllRows<RiskSignalRow>((from, to) => {
        let q = supabase
          .from('risk_signals')
          .select('id, entity_type, entity_id, rule_id, severity, detected_at, resolved_at, evidence, ai_output_id, previous_signal_id')
          .order('detected_at', { ascending: false });
        if (tenantId) q = q.eq('tenant_id', tenantId);
        return q.range(from, to);
      }),
      fetchAllRows<TaskRow>((from, to) =>
        supabase
          .from('tasks_sync')
          .select('external_id, title, status, due_date, project_external_id, release_id')
          .range(from, to),
      ),
      supabase.from('releases').select('id, name'),
      supabase.from('projects_sync').select('id, external_id, name, budget, spent, team_data').order('name'),
      supabase.from('risk_factor_overrides').select('project_id, technical_complexity, scope_creep'),
    ]);

    setRawSignals(signalsData);
    setTasks(tasksData);
    setReleases((releasesRes.data ?? []) as ReleaseRow[]);
    setProjects((projectsRes.data ?? []) as ProjectRow[]);

    const overrideMap: Record<string, OverrideRow> = {};
    for (const row of (overridesRes.data ?? []) as OverrideRow[]) {
      overrideMap[row.project_id] = row;
    }
    setOverrides(overrideMap);

    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // ============================================================
  // Entity-id attribution: which risk_signals rows belong to which
  // project. Tasks carry project_external_id directly. Releases have no
  // project FK at all (releases is UNIQUE(tenant_id, name) with no
  // project reference) — a release is attributed to every project that
  // has at least one task in it, via tasks_sync.release_id. This mirrors
  // (but is not identical to) generate-risk-mitigation-v2's own entity-set
  // logic, which only considers [project.id, project.external_id,
  // ...taskExternalIds] and omits release ids — so the Actions tab may
  // reason over slightly fewer signals than the Overview/Why tabs display
  // for the same project. That divergence is inherent to the edge
  // function's contract, not a bug here.
  // ============================================================
  const tasksByProjectExt = useMemo(() => {
    const map = new Map<string, TaskRow[]>();
    for (const t of tasks) {
      if (!t.project_external_id) continue;
      if (!map.has(t.project_external_id)) map.set(t.project_external_id, []);
      map.get(t.project_external_id)!.push(t);
    }
    return map;
  }, [tasks]);

  const releaseProjectExtIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const t of tasks) {
      if (!t.release_id || !t.project_external_id) continue;
      if (!map.has(t.release_id)) map.set(t.release_id, new Set());
      map.get(t.release_id)!.add(t.project_external_id);
    }
    return map;
  }, [tasks]);

  const entityIdsByProject = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const p of projects) {
      const projectTasks = tasksByProjectExt.get(p.external_id) ?? [];
      const projectReleaseIds = releases
        .filter((r) => releaseProjectExtIds.get(r.id)?.has(p.external_id))
        .map((r) => r.id);
      map.set(
        p.id,
        new Set([p.id, p.external_id, ...projectTasks.map((t) => t.external_id), ...projectReleaseIds]),
      );
    }
    return map;
  }, [projects, tasksByProjectExt, releases, releaseProjectExtIds]);

  // Latest row per (entity_type, entity_id, rule_id) — dedup BEFORE
  // filtering resolved_at, since evaluate-risk-rules inserts a new row on
  // every worsening rather than updating in place; the superseded older
  // row can still have resolved_at IS NULL alongside the new one.
  const dedupedSignals = useMemo(() => dedupLatestPerKey(rawSignals), [rawSignals]);

  const signalsForProject = useCallback(
    (projectId: string) => {
      const ids = entityIdsByProject.get(projectId);
      if (!ids) return [];
      return dedupedSignals.filter((s) => ids.has(s.entity_id));
    },
    [dedupedSignals, entityIdsByProject],
  );

  const rawSignalsForProject = useCallback(
    (projectId: string) => {
      const ids = entityIdsByProject.get(projectId);
      if (!ids) return [];
      return rawSignals.filter((s) => ids.has(s.entity_id));
    },
    [rawSignals, entityIdsByProject],
  );

  // Portfolio-wide workload normalization: each project's open-task /
  // team-size ratio, scaled against the highest ratio across the
  // portfolio so no project can show more than 100. Not a fabricated
  // number — it's the same ratio for every project, just expressed
  // relative to the busiest one instead of an invented fixed scale.
  const maxWorkloadRatio = useMemo(() => {
    let max = 0;
    for (const p of projects) {
      const projectTasks = tasksByProjectExt.get(p.external_id) ?? [];
      const openCount = projectTasks.filter((t) => t.status !== 'done').length;
      const teamSize = Array.isArray(p.team_data) ? p.team_data.length : 0;
      if (teamSize > 0) max = Math.max(max, openCount / teamSize);
    }
    return max;
  }, [projects, tasksByProjectExt]);

  const projectSummaries = useMemo(() => {
    const now = new Date();
    return projects
      .map((project) => {
        const allProjectSignals = signalsForProject(project.id);
        const openSignals = allProjectSignals.filter((s) => s.resolved_at === null);
        const maxOpenSeverity = openSignals.reduce<RiskSignalRow['severity'] | null>((max, s) => {
          if (!max) return s.severity;
          return SEVERITY_SCORE[s.severity] > SEVERITY_SCORE[max] ? s.severity : max;
        }, null);
        const score = maxOpenSeverity ? SEVERITY_SCORE[maxOpenSeverity] : 0;

        const openBySeverity = { critical: 0, high: 0, medium: 0, low: 0 };
        for (const s of openSignals) openBySeverity[s.severity]++;

        const projectTasks = tasksByProjectExt.get(project.external_id) ?? [];
        const tasksTotal = projectTasks.length;
        const tasksOverdue = projectTasks.filter(
          (t) => t.status !== 'done' && t.due_date && new Date(t.due_date) < now,
        ).length;

        const lastDetected = allProjectSignals.length
          ? allProjectSignals.reduce((latest, s) => (s.detected_at > latest ? s.detected_at : latest), allProjectSignals[0].detected_at)
          : null;

        const aiBriefsWith = allProjectSignals.filter((s) => s.ai_output_id !== null).length;

        return {
          project,
          score,
          openSignals,
          allSignals: allProjectSignals,
          openBySeverity,
          tasksTotal,
          tasksOverdue,
          lastDetected,
          aiBriefsWith,
          aiBriefsTotal: allProjectSignals.length,
        };
      })
      .sort((a, b) => b.score - a.score || a.project.name.localeCompare(b.project.name));
  }, [projects, signalsForProject, tasksByProjectExt]);

  useEffect(() => {
    setSelectedProjectId((prev) => {
      if (prev && projectSummaries.some((p) => p.project.id === prev)) return prev;
      return projectSummaries[0]?.project.id ?? null;
    });
  }, [projectSummaries]);

  const selected = projectSummaries.find((p) => p.project.id === selectedProjectId) ?? null;

  // Signals that attribute to at least one project — the population every
  // project card / tab actually shows. A release with no synced tasks, or
  // a task with a null project_external_id, produces a risk_signals row
  // that can't be attributed to any project and would otherwise silently
  // vanish from the UI while still counting toward a portfolio total.
  // Track it explicitly instead of letting the two numbers drift apart.
  const attributedSignalIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of projectSummaries) for (const s of p.allSignals) ids.add(s.id);
    return ids;
  }, [projectSummaries]);
  const attributedDedupedSignals = useMemo(
    () => dedupedSignals.filter((s) => attributedSignalIds.has(s.id)),
    [dedupedSignals, attributedSignalIds],
  );
  const unattributedSignalCount = dedupedSignals.length - attributedDedupedSignals.length;

  // Radar factor inputs for the selected project (Factors tab).
  const selectedDependenciesScore = useMemo(() => {
    if (!selected) return 0;
    const blocked = selected.openSignals.filter((s) => s.rule_id === 'blocked_dependency');
    if (blocked.length === 0) return 0;
    const maxSeverity = blocked.reduce((max, s) => (SEVERITY_SCORE[s.severity] > SEVERITY_SCORE[max] ? s.severity : max), blocked[0].severity);
    return SEVERITY_SCORE[maxSeverity];
  }, [selected]);

  const selectedBudget: BudgetFactor = useMemo(() => {
    if (!selected || !selected.project.budget || selected.project.budget <= 0) {
      return { ratioPct: 0, hasBudgetData: false };
    }
    const spent = selected.project.spent ?? 0;
    return { ratioPct: Math.max(0, Math.min(100, Math.round((spent / selected.project.budget) * 100))), hasBudgetData: true };
  }, [selected]);

  const selectedWorkload: WorkloadFactor = useMemo(() => {
    if (!selected) return { openTasks: 0, teamSize: 0, ratio: null, normalizedScore: 0 };
    const projectTasks = tasksByProjectExt.get(selected.project.external_id) ?? [];
    const openTasks = projectTasks.filter((t) => t.status !== 'done').length;
    const teamSize = Array.isArray(selected.project.team_data) ? selected.project.team_data.length : 0;
    const ratio = teamSize > 0 ? openTasks / teamSize : null;
    const normalizedScore = ratio !== null && maxWorkloadRatio > 0 ? Math.round((ratio / maxWorkloadRatio) * 100) : 0;
    return { openTasks, teamSize, ratio, normalizedScore };
  }, [selected, tasksByProjectExt, maxWorkloadRatio]);

  // ============================================================
  // Portfolio Risk Trend (monthly) — state reconstruction, not event
  // bucketing. "Avg Risk" and "High Risk Projects" are point-in-time
  // states, so each month asks "what was open as of the end of this
  // month" against the deduped findings (detected_at <= monthEnd AND
  // (resolved_at is null OR resolved_at > monthEnd)), using each
  // finding's current/latest known severity for its whole open lifetime
  // — an approximation, since intermediate severity changes on the same
  // (entity,rule) key are collapsed into the newest row by dedup.
  // "Mitigated" counts deduped findings whose resolved_at falls inside
  // that month. The month sequence is continuous from the earliest
  // detection to the current month so the chart never silently skips an
  // empty month.
  // ============================================================
  const monthlyTrend = useMemo<MonthlyTrendPoint[]>(() => {
    if (dedupedSignals.length === 0) return [];
    const earliest = dedupedSignals.reduce((min, s) => (s.detected_at < min ? s.detected_at : min), dedupedSignals[0].detected_at);
    const start = startOfMonth(new Date(earliest));
    const end = startOfMonth(new Date());

    const points: MonthlyTrendPoint[] = [];
    let cursor = start;
    while (cursor <= end) {
      const monthEnd = endOfMonth(cursor);

      const openAsOfMonth = dedupedSignals.filter(
        (s) => new Date(s.detected_at) <= monthEnd && (s.resolved_at === null || new Date(s.resolved_at) > monthEnd),
      );

      const projectScores = projects.map((p) => {
        const ids = entityIdsByProject.get(p.id);
        if (!ids) return 0;
        const projectOpen = openAsOfMonth.filter((s) => ids.has(s.entity_id));
        if (projectOpen.length === 0) return 0;
        const maxSeverity = projectOpen.reduce((max, s) => (SEVERITY_SCORE[s.severity] > SEVERITY_SCORE[max] ? s.severity : max), projectOpen[0].severity);
        return SEVERITY_SCORE[maxSeverity];
      });

      const avgRisk = projectScores.length ? Math.round(projectScores.reduce((sum, s) => sum + s, 0) / projectScores.length) : 0;
      const highRiskProjects = projectScores.filter((s) => s >= 70).length;
      const mitigated = dedupedSignals.filter(
        (s) => s.resolved_at !== null && new Date(s.resolved_at) >= cursor && new Date(s.resolved_at) <= monthEnd,
      ).length;

      points.push({ month: format(cursor, 'MMM yyyy'), avgRisk, highRiskProjects, mitigated });
      cursor = addMonths(cursor, 1);
    }
    return points;
  }, [dedupedSignals, projects, entityIdsByProject]);

  const monthsWithEvents = useMemo(() => {
    const months = new Set<string>();
    for (const s of dedupedSignals) {
      months.add(format(new Date(s.detected_at), 'yyyy-MM'));
      if (s.resolved_at) months.add(format(new Date(s.resolved_at), 'yyyy-MM'));
    }
    return months.size;
  }, [dedupedSignals]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground masira-fade-in">
        <ShieldAlert className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm font-medium text-foreground">No projects synced yet</p>
        <p className="text-xs mt-1">The risk engine evaluates overdue work, blocked dependencies, and release health after every Jira sync.</p>
      </div>
    );
  }

  const avgPortfolioRisk = projectSummaries.length
    ? Math.round(projectSummaries.reduce((sum, p) => sum + p.score, 0) / projectSummaries.length)
    : 0;
  const criticalCount = projectSummaries.filter((p) => getRiskLabel(p.score) === 'Critical').length;
  const atRiskCount = projectSummaries.filter((p) => getRiskLabel(p.score) === 'At Risk').length;
  const activeFindings = projectSummaries.reduce((sum, p) => sum + p.openSignals.length, 0);
  const portfolioAiBriefsWith = attributedDedupedSignals.filter((s) => s.ai_output_id !== null).length;
  const portfolioAiBriefsTotal = attributedDedupedSignals.length;

  return (
    <div className="space-y-6">
      <div className="nexus-fade-in flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">Risks</h2>
          </div>
          <p className="text-muted-foreground mt-1">Material risks derived from synced Jira signals</p>
        </div>
        <Badge variant="outline" className="gap-1 px-3 py-1">
          <Sparkles className="h-3 w-3 text-primary" />
          <span>Deterministic + Evidence-based</span>
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="nexus-fade-in" style={{ animationDelay: '0.1s' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Portfolio Risk</p>
                <p className={cn('text-3xl font-bold mt-1', getRiskColor(avgPortfolioRisk))}>{avgPortfolioRisk}</p>
              </div>
              <div className={cn('p-3 rounded-full', getRiskBgColor(avgPortfolioRisk))}>
                <Gauge className={cn('h-6 w-6', getRiskColor(avgPortfolioRisk))} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Across {projectSummaries.length} projects</p>
          </CardContent>
        </Card>

        <Card className="nexus-fade-in" style={{ animationDelay: '0.15s' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Critical Risk</p>
                <p className="text-3xl font-bold mt-1 text-destructive">{criticalCount}</p>
              </div>
              <div className="p-3 rounded-full bg-destructive/20">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Projects need attention</p>
          </CardContent>
        </Card>

        <Card className="nexus-fade-in" style={{ animationDelay: '0.2s' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">At Risk</p>
                <p className="text-3xl font-bold mt-1 text-amber-500">{atRiskCount}</p>
              </div>
              <div className="p-3 rounded-full bg-amber-500/20">
                <Activity className="h-6 w-6 text-amber-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Monitoring required</p>
          </CardContent>
        </Card>

        <Card className="nexus-fade-in" style={{ animationDelay: '0.25s' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Active Findings</p>
                <p className="text-3xl font-bold mt-1 text-primary">{activeFindings}</p>
              </div>
              <div className="p-3 rounded-full bg-primary/20">
                <ListChecks className="h-6 w-6 text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Open rule firings</p>
          </CardContent>
        </Card>

        <Card className="nexus-fade-in" style={{ animationDelay: '0.3s' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">AI Briefs</p>
                <p className="text-3xl font-bold mt-1 text-primary">{portfolioAiBriefsWith} / {portfolioAiBriefsTotal}</p>
              </div>
              <div className="p-3 rounded-full bg-primary/20">
                <Brain className="h-6 w-6 text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Signals with a generated brief</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 nexus-fade-in" style={{ animationDelay: '0.35s' }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Project Risk Overview</CardTitle>
            {unattributedSignalCount > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {unattributedSignalCount} signal{unattributedSignalCount === 1 ? '' : 's'} could not be matched to a project (release with no synced tasks, or a task with no project reference) and are not shown below.
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {projectSummaries.map((p) => (
              <div
                key={p.project.id}
                onClick={() => setSelectedProjectId(p.project.id)}
                className={cn(
                  'p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md',
                  selectedProjectId === p.project.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{p.project.name}</p>
                    <Badge variant="secondary" className={cn('text-xs mt-1', getRiskBgColor(p.score), getRiskColor(p.score))}>
                      {getRiskLabel(p.score)}
                    </Badge>
                  </div>
                  <RiskGauge score={p.score} size="small" />
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Tasks overdue: {p.tasksOverdue} of {p.tasksTotal}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 nexus-fade-in" style={{ animationDelay: '0.4s' }}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">{selected?.project.name}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Per-project risk analysis</p>
              </div>
              {selected && (
                <Badge className={cn(getRiskBgColor(selected.score), getRiskColor(selected.score))}>
                  {getRiskLabel(selected.score)}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selected ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Select a project to view its risk analysis.</div>
            ) : (
              // Keyed on project id: RiskFactorsPanel/RiskMitigationPanel/
              // RiskWhyPanel all hold their own per-project state (rating
              // form inputs, generated suggestions, open brief dialog).
              // Radix Tabs only unmounts the INACTIVE TabsContent, so
              // switching the selected project while staying on the same
              // tab would otherwise reuse those instances and leak the
              // previous project's state onto the new one (e.g. saving
              // project A's factor ratings onto project B). Keying resets
              // to the Overview tab on every project switch, which is an
              // acceptable trade for never showing stale cross-project data.
              <Tabs key={selected.project.id} defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="factors">Factors</TabsTrigger>
                  <TabsTrigger value="trend">Trend</TabsTrigger>
                  <TabsTrigger value="why" className="gap-1"><Brain className="h-3 w-3" /> Why</TabsTrigger>
                  <TabsTrigger value="actions" className="gap-1"><Shield className="h-3 w-3" /> Actions</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col items-center p-4 rounded-lg bg-muted/50">
                      <RiskGauge score={selected.score} size="large" />
                      <Badge className={cn('mt-2', getRiskBgColor(selected.score), getRiskColor(selected.score))}>
                        {getRiskLabel(selected.score)}
                      </Badge>
                    </div>

                    <div className="space-y-4">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <ListChecks className="h-4 w-4" />
                          <span className="text-xs uppercase tracking-wide">Tasks Overdue</span>
                        </div>
                        <p className={cn('text-2xl font-bold', selected.tasksOverdue > 0 ? 'text-amber-500' : 'text-emerald-500')}>
                          {selected.tasksOverdue} of {selected.tasksTotal}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Clock className="h-4 w-4" />
                          <span className="text-xs uppercase tracking-wide">Last Detected</span>
                        </div>
                        <p className="text-lg font-bold text-foreground">
                          {selected.lastDetected ? new Date(selected.lastDetected).toLocaleDateString() : 'No findings'}
                        </p>
                      </div>
                    </div>

                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Open Signals by Severity</p>
                      <div className="space-y-1.5">
                        {(['critical', 'high', 'medium', 'low'] as const).map((sev) => (
                          <div key={sev} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1.5 capitalize text-muted-foreground">
                              <span className={cn('h-2 w-2 rounded-full', getSeverityDotColor(sev))} />
                              {sev}
                            </span>
                            <span className="font-medium">{selected.openBySeverity[sev]}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        AI Briefs: {selected.aiBriefsWith} of {selected.aiBriefsTotal}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium">Open Findings</p>
                      {selected.openSignals.length > 5 && (
                        <span className="text-xs text-muted-foreground">Showing 5 of {selected.openSignals.length} — see the Why tab for all</span>
                      )}
                    </div>
                    {selected.openSignals.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No open findings for this project.</p>
                    ) : (
                      <div className="space-y-2">
                        {selected.openSignals.slice(0, 5).map((s) => (
                          <div key={s.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <Badge className={cn('text-xs capitalize', getSeverityBadgeColor(s.severity))}>{s.severity}</Badge>
                              <span>{humanizeRule(s.rule_id)}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">{new Date(s.detected_at).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="factors" className="mt-4">
                  <RiskFactorsPanel
                    projectId={selected.project.id}
                    dependenciesScore={selectedDependenciesScore}
                    budget={selectedBudget}
                    workload={selectedWorkload}
                    override={overrides[selected.project.id] ?? null}
                    canManage={canManage}
                    onSaved={load}
                  />
                </TabsContent>

                <TabsContent value="trend" className="mt-4">
                  <ProjectRiskTrend signals={rawSignalsForProject(selected.project.id)} />
                </TabsContent>

                <TabsContent value="why" className="mt-4">
                  <RiskWhyPanel signals={selected.openSignals} onDecided={load} />
                </TabsContent>

                <TabsContent value="actions" className="mt-4">
                  <RiskMitigationPanel projectId={selected.project.id} />
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="nexus-fade-in" style={{ animationDelay: '0.45s' }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Portfolio Risk Trend (Monthly)</CardTitle>
        </CardHeader>
        <CardContent>
          {monthsWithEvents < 2 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Insufficient history yet — the monthly trend needs detected or mitigated signals spanning at least two calendar months.
            </div>
          ) : (
            <div className="h-64">
              <ChartContainer config={monthlyChartConfig}>
                <LineChart data={monthlyTrend}>
                  <XAxis dataKey="month" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} axisLine={{ stroke: 'hsl(var(--border))' }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} axisLine={{ stroke: 'hsl(var(--border))' }} allowDecimals={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Line type="monotone" dataKey="avgRisk" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: 'hsl(var(--primary))' }} name="Avg Risk" />
                  <Line type="monotone" dataKey="highRiskProjects" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ fill: 'hsl(var(--destructive))' }} name="High Risk Projects" />
                  <Line type="monotone" dataKey="mitigated" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} name="Risks Mitigated" />
                </LineChart>
              </ChartContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RiskPredictionView;
